import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  FileText,
  Download,
  Share2,
  ChevronRight,
  TrendingUp,
  DollarSign,
  Eye,
  MousePointer,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { campaignsAPI, reportsAPI, referenceAPI, storiesAPI } from '../lib/api';
import { formatDate, formatNumber, formatCurrency, formatPercentage, getChannelLabel } from '../lib/utils';
import { toast } from 'sonner';
import { CalendarIcon } from 'lucide-react';
import { cn } from '../lib/utils';

function MetricCard({ label, value, prefix = '', suffix = '' }) {
  return (
    <div className="bg-bg-tertiary p-4 rounded-lg">
      <p className="text-xs text-text-tertiary uppercase mb-1">{label}</p>
      <p className="text-xl font-heading font-bold text-text-primary">
        {prefix}{value !== null && value !== undefined ? value : '-'}{suffix}
      </p>
    </div>
  );
}

export function Analytics() {
  const [activeTab, setActiveTab] = useState('campaigns');
  
  // Campaign Analytics State
  const [campaigns, setCampaigns] = useState([]);
  const [campaignTotals, setCampaignTotals] = useState({});
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  // Reports State
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Reference Data
  const [clients, setClients] = useState([]);
  const [stories, setStories] = useState([]);
  
  // Filters
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedChannel, setSelectedChannel] = useState('all');
  
  // Campaign Form State
  const [campaignForm, setCampaignForm] = useState({
    story_id: '',
    client_id: '',
    channel: '',
    campaign_name: '',
    impressions: 0,
    clicks: 0,
    spend: '',
    conversions: 0,
    reach: 0,
    engagement: 0,
    period_start: null,
    period_end: null,
    notes: '',
  });

  // Report Form State
  const [reportForm, setReportForm] = useState({
    client_id: '',
    report_name: '',
    period_start: null,
    period_end: null,
    tone: 'professional',
    custom_instructions: '',
  });

  useEffect(() => {
    loadReferenceData();
    loadCampaigns();
    loadReports();
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [selectedClient, selectedChannel]);

  const loadReferenceData = async () => {
    try {
      const [clientsRes, storiesRes] = await Promise.all([
        referenceAPI.getClients(),
        storiesAPI.getAll({ status: 'published', page_size: 100 }),
      ]);
      setClients(clientsRes.data);
      setStories(storiesRes.data.stories);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const loadCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const params = {
        ...(selectedClient !== 'all' && { client_id: selectedClient }),
        ...(selectedChannel !== 'all' && { channel: selectedChannel }),
      };
      const response = await campaignsAPI.getAll(params);
      setCampaigns(response.data.campaigns || []);
      setCampaignTotals(response.data.totals || {});
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setReportsLoading(true);
      const response = await reportsAPI.getAll();
      setReports(response.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleSaveCampaign = async () => {
    try {
      const data = {
        ...campaignForm,
        period_start: campaignForm.period_start?.toISOString().split('T')[0],
        period_end: campaignForm.period_end?.toISOString().split('T')[0],
        spend: campaignForm.spend ? parseFloat(campaignForm.spend) : null,
        story_id: campaignForm.story_id || null,
        client_id: campaignForm.client_id || null,
      };

      if (editingCampaign) {
        await campaignsAPI.update(editingCampaign.id, data);
        toast.success('Campaign updated');
      } else {
        await campaignsAPI.create(data);
        toast.success('Campaign created');
      }

      setShowCampaignModal(false);
      setEditingCampaign(null);
      resetCampaignForm();
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await campaignsAPI.delete(id);
      toast.success('Campaign deleted');
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      const data = {
        ...reportForm,
        period_start: reportForm.period_start?.toISOString().split('T')[0],
        period_end: reportForm.period_end?.toISOString().split('T')[0],
      };

      await reportsAPI.generate(data);
      toast.success('Report generated');
      setShowReportModal(false);
      resetReportForm();
      loadReports();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      story_id: '',
      client_id: '',
      channel: '',
      campaign_name: '',
      impressions: 0,
      clicks: 0,
      spend: '',
      conversions: 0,
      reach: 0,
      engagement: 0,
      period_start: null,
      period_end: null,
      notes: '',
    });
  };

  const resetReportForm = () => {
    setReportForm({
      client_id: '',
      report_name: '',
      period_start: null,
      period_end: null,
      tone: 'professional',
      custom_instructions: '',
    });
  };

  const openEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      story_id: campaign.story_id || '',
      client_id: campaign.client_id || '',
      channel: campaign.channel || '',
      campaign_name: campaign.campaign_name || '',
      impressions: campaign.impressions || 0,
      clicks: campaign.clicks || 0,
      spend: campaign.spend?.toString() || '',
      conversions: campaign.conversions || 0,
      reach: campaign.reach || 0,
      engagement: campaign.engagement || 0,
      period_start: campaign.period_start ? new Date(campaign.period_start) : null,
      period_end: campaign.period_end ? new Date(campaign.period_end) : null,
      notes: campaign.notes || '',
    });
    setShowCampaignModal(true);
  };

  // Compute live CTR
  const liveCTR = campaignForm.impressions > 0 
    ? ((campaignForm.clicks / campaignForm.impressions) * 100).toFixed(2) 
    : '0.00';

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary uppercase tracking-tight">
          Analytics & Reports
        </h1>
        <p className="text-text-secondary mt-1">Campaign performance and client reporting</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-bg-tertiary">
          <TabsTrigger value="campaigns">Campaign Analytics</TabsTrigger>
          <TabsTrigger value="reports">Client Reports</TabsTrigger>
        </TabsList>

        {/* Campaign Analytics Tab */}
        <TabsContent value="campaigns" className="space-y-6 mt-6">
          {/* Summary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <MetricCard label="Total Impressions" value={formatNumber(campaignTotals.total_impressions)} />
            <MetricCard label="Total Clicks" value={formatNumber(campaignTotals.total_clicks)} />
            <MetricCard label="Blended CTR" value={campaignTotals.blended_ctr ? (campaignTotals.blended_ctr * 100).toFixed(2) : '-'} suffix="%" />
            <MetricCard label="Total Spend" value={campaignTotals.total_spend?.toFixed(2)} prefix="£" />
            <MetricCard label="CPC" value={campaignTotals.cpc?.toFixed(2)} prefix="£" />
          </div>

          {/* Filters & Actions */}
          <div className="flex items-center justify-between">
            <div className="flex gap-4">
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="w-[180px] bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                <SelectTrigger className="w-[180px] bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="All Channels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="google_ads">Google Ads</SelectItem>
                  <SelectItem value="linkedin_ads">LinkedIn Ads</SelectItem>
                  <SelectItem value="meta_ads">Meta Ads</SelectItem>
                  <SelectItem value="organic">Organic</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={() => setShowCampaignModal(true)} data-testid="add-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              Log Campaign Data
            </Button>
          </div>

          {/* Campaign Table */}
          <Card className="bg-surface-card border-border-default">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Story</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Impressions</TableHead>
                    <TableHead>Clicks</TableHead>
                    <TableHead>CTR</TableHead>
                    <TableHead>Spend</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignsLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-text-tertiary">
                        No campaigns found
                      </TableCell>
                    </TableRow>
                  ) : (
                    campaigns.map((campaign) => (
                      <TableRow key={campaign.id} className="table-row">
                        <TableCell className="table-cell max-w-[200px] truncate">
                          {campaign.story_headline || '-'}
                        </TableCell>
                        <TableCell className="table-cell">{campaign.client_name || '-'}</TableCell>
                        <TableCell className="table-cell">
                          <Badge variant="outline" className="capitalize">
                            {getChannelLabel(campaign.channel)}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">{campaign.campaign_name || '-'}</TableCell>
                        <TableCell className="table-cell">{formatNumber(campaign.impressions)}</TableCell>
                        <TableCell className="table-cell">{formatNumber(campaign.clicks)}</TableCell>
                        <TableCell className="table-cell">
                          {campaign.ctr ? `${(campaign.ctr * 100).toFixed(2)}%` : '-'}
                        </TableCell>
                        <TableCell className="table-cell">
                          {campaign.spend ? `£${campaign.spend.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell className="table-cell">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditCampaign(campaign)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCampaign(campaign.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Client Reports Tab */}
        <TabsContent value="reports" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Button onClick={() => setShowReportModal(true)} data-testid="generate-report-btn">
              <Plus className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </div>

          <Card className="bg-surface-card border-border-default">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Report Name</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-text-tertiary">
                        No reports generated yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    reports.map((report) => (
                      <TableRow key={report.id} className="table-row">
                        <TableCell className="table-cell font-medium">{report.report_name}</TableCell>
                        <TableCell className="table-cell">{report.client_name || '-'}</TableCell>
                        <TableCell className="table-cell">
                          {formatDate(report.period_start)} - {formatDate(report.period_end)}
                        </TableCell>
                        <TableCell className="table-cell">
                          <Badge 
                            className={cn(
                              'capitalize',
                              report.status === 'finalised' && 'bg-green-900/20 text-green-400',
                              report.status === 'draft' && 'bg-amber-900/20 text-amber-400',
                              report.status === 'sent' && 'bg-blue-900/20 text-blue-400'
                            )}
                          >
                            {report.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">{formatDate(report.created_at)}</TableCell>
                        <TableCell className="table-cell">
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Share2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="sm:max-w-lg bg-surface-card border-border-default">
          <DialogHeader>
            <DialogTitle className="text-text-primary">
              {editingCampaign ? 'Edit Campaign Data' : 'Log Campaign Data'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Story</Label>
                <Select 
                  value={campaignForm.story_id} 
                  onValueChange={(v) => setCampaignForm({...campaignForm, story_id: v})}
                >
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue placeholder="Select story" />
                  </SelectTrigger>
                  <SelectContent>
                    {stories.map((story) => (
                      <SelectItem key={story.id} value={story.id}>
                        {story.headline?.substring(0, 50)}...
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Channel</Label>
                <Select 
                  value={campaignForm.channel} 
                  onValueChange={(v) => setCampaignForm({...campaignForm, channel: v})}
                >
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue placeholder="Select channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google_ads">Google Ads</SelectItem>
                    <SelectItem value="linkedin_ads">LinkedIn Ads</SelectItem>
                    <SelectItem value="meta_ads">Meta Ads</SelectItem>
                    <SelectItem value="organic">Organic</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignForm.campaign_name}
                onChange={(e) => setCampaignForm({...campaignForm, campaign_name: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Impressions</Label>
                <Input
                  type="number"
                  value={campaignForm.impressions}
                  onChange={(e) => setCampaignForm({...campaignForm, impressions: parseInt(e.target.value) || 0})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Clicks</Label>
                <Input
                  type="number"
                  value={campaignForm.clicks}
                  onChange={(e) => setCampaignForm({...campaignForm, clicks: parseInt(e.target.value) || 0})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>

            <div className="bg-accent-primary/10 p-3 rounded-lg text-center">
              <p className="text-sm text-text-secondary">Live CTR:</p>
              <p className="text-2xl font-heading font-bold text-accent-primary">{liveCTR}%</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Spend (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={campaignForm.spend}
                  onChange={(e) => setCampaignForm({...campaignForm, spend: e.target.value})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Conversions</Label>
                <Input
                  type="number"
                  value={campaignForm.conversions}
                  onChange={(e) => setCampaignForm({...campaignForm, conversions: parseInt(e.target.value) || 0})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-bg-tertiary border-border-default">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {campaignForm.period_start ? format(campaignForm.period_start, 'PPP') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={campaignForm.period_start}
                      onSelect={(date) => setCampaignForm({...campaignForm, period_start: date})}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Period End</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-bg-tertiary border-border-default">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {campaignForm.period_end ? format(campaignForm.period_end, 'PPP') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={campaignForm.period_end}
                      onSelect={(date) => setCampaignForm({...campaignForm, period_end: date})}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCampaign} data-testid="save-campaign-btn">
              {editingCampaign ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Generation Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="sm:max-w-lg bg-surface-card border-border-default">
          <DialogHeader>
            <DialogTitle className="text-text-primary">Generate Client Report</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Client</Label>
              <Select 
                value={reportForm.client_id} 
                onValueChange={(v) => setReportForm({...reportForm, client_id: v})}
              >
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Report Name</Label>
              <Input
                value={reportForm.report_name}
                onChange={(e) => setReportForm({...reportForm, report_name: e.target.value})}
                placeholder="e.g., Q4 2025 Performance Report"
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-bg-tertiary border-border-default">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {reportForm.period_start ? format(reportForm.period_start, 'PPP') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={reportForm.period_start}
                      onSelect={(date) => setReportForm({...reportForm, period_start: date})}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Period End</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-bg-tertiary border-border-default">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {reportForm.period_end ? format(reportForm.period_end, 'PPP') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={reportForm.period_end}
                      onSelect={(date) => setReportForm({...reportForm, period_end: date})}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div>
              <Label>Tone</Label>
              <Select 
                value={reportForm.tone} 
                onValueChange={(v) => setReportForm({...reportForm, tone: v})}
              >
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="executive">Executive Summary</SelectItem>
                  <SelectItem value="detailed">Detailed Breakdown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Custom Instructions (Optional)</Label>
              <Textarea
                value={reportForm.custom_instructions}
                onChange={(e) => setReportForm({...reportForm, custom_instructions: e.target.value})}
                placeholder="Any specific focus areas or notes for the AI..."
                className="bg-bg-tertiary border-border-default"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateReport} 
              disabled={generatingReport || !reportForm.client_id || !reportForm.report_name}
              data-testid="confirm-generate-report-btn"
            >
              {generatingReport ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Analytics;
